/**
 * Base class for AuthWorks web components
 *
 * Provides:
 * - Shadow DOM setup
 * - Adopted stylesheets pattern
 * - Attribute observation helpers
 * - Event dispatch helpers
 */

export abstract class AWElement extends HTMLElement {
  protected shadow: ShadowRoot;
  private static styleSheet: CSSStyleSheet | null = null;

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });
  }

  /**
   * Override to provide component styles
   */
  protected static get styles(): string {
    return '';
  }

  /**
   * Get or create the shared stylesheet for this component class
   */
  protected getStyleSheet(): CSSStyleSheet {
    const ctor = this.constructor as typeof AWElement;
    if (!ctor.styleSheet) {
      ctor.styleSheet = new CSSStyleSheet();
      ctor.styleSheet.replaceSync(ctor.styles);
    }
    return ctor.styleSheet;
  }

  /**
   * Apply styles using adopted stylesheets (performant, shared across instances)
   */
  protected applyStyles(): void {
    const sheet = this.getStyleSheet();
    this.shadow.adoptedStyleSheets = [sheet];
  }

  /**
   * Render the component - override in subclass
   */
  protected abstract render(): string;

  /**
   * Update the shadow DOM content
   */
  protected update(): void {
    // Get non-style content
    const content = this.render();

    // Only update if content changed
    const currentContent = this.shadow.innerHTML;
    if (currentContent !== content) {
      this.shadow.innerHTML = content;
    }
  }

  /**
   * Dispatch a custom event with standard options
   */
  protected emit<T>(name: string, detail?: T): void {
    this.dispatchEvent(new CustomEvent(name, {
      detail,
      bubbles: true,
      composed: true // crosses shadow DOM boundary
    }));
  }

  /**
   * Get boolean attribute
   */
  protected getBoolAttr(name: string): boolean {
    return this.hasAttribute(name);
  }

  /**
   * Set boolean attribute
   */
  protected setBoolAttr(name: string, value: boolean): void {
    if (value) {
      this.setAttribute(name, '');
    } else {
      this.removeAttribute(name);
    }
  }

  connectedCallback(): void {
    this.applyStyles();
    this.update();
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue !== newValue) {
      this.update();
    }
  }
}
